import styled from '@emotion/styled';
import {
  TextDisplayAlt,
  TextParagraph,
  token,
} from '@angeplus/prever-component-lib';
import { ComponentProps, useEffect, useRef, useState } from 'react';

import { BENEFITS } from './benefits';
import { BenefitsCard } from './BenefitsCard';

const CARDS_QUANTITY = BENEFITS.length;
const MOBILE_BREAKPOINT = 425;

export const BenefitsSection = () => {
  const ref = useRef<HTMLDivElement>();
  const [scrollTop, setScrollTop] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;

    const handleScroll = () => {
      setScrollTop(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) return;

    const pageScrollPercentage = scrollTop / window.innerHeight;
    const elementScrollPercentage =
      pageScrollPercentage < 1 ? 0 : pageScrollPercentage - 1;
    const width = ref.current?.offsetWidth ?? 0 / CARDS_QUANTITY;

    if (ref.current) {
      ref.current.scrollLeft = width * elementScrollPercentage;
    }
  }, [scrollTop, isMobile]);

  return (
    <SectionWrapper>
      <SectionContainer id="beneficios">
        <SectionHeader>
          <SectionTitle>
            Seus benefícios, sua família, sua vida digital
          </SectionTitle>
          <SectionDescription>
            Com o <strong>Angeplus</strong>, você gerencia a saúde, aproveita
            descontos e acessa diversos benefícios em um só lugar.
          </SectionDescription>
        </SectionHeader>

        <Carousel ref={ref as ComponentProps<typeof Carousel>['ref']}>
          {BENEFITS.map((benefit) => (
            <BenefitsCard key={benefit.id} benefit={benefit} />
          ))}
        </Carousel>
      </SectionContainer>
    </SectionWrapper>
  );
};

const SectionWrapper = styled.div`
  position: relative;
  width: 100%;
  height: auto;

  @media (min-width: 425px) {
    height: calc(${CARDS_QUANTITY} * 100vh);
  }
`;

const SectionContainer = styled.section`
  padding: 64px 32px;
  display: flex;
  flex-direction: column;
  /* gap: ${token.spacing.spacingLarge}; */
  position: sticky;
  top: 66px;

  @media (min-width: 768px) {
    top: 72px;
    padding: ${token.spacing.spacingXxLarge} 100px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: ${token.spacing.spacingMedium};
`;

const SectionTitle = styled(TextDisplayAlt)`
  color: #082340 !important;
  font-size: 28px;
  line-height: 1.2;

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const SectionDescription = styled(TextParagraph)`
  color: #082340 !important;
  font-size: 16px;
  line-height: 1.6;

  @media (min-width: 768px) {
    font-size: 22px;
  }
`;

const Carousel = styled.div`
  position: static;
  bottom: 100px;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding: ${token.spacing.spacingMedium};
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  display: flex;
  gap: ${token.spacing.spacingMedium};

  @media (min-width: 768px) {
    padding: ${token.spacing.spacingLarge} ${token.spacing.spacingXxLarge};
    gap: ${token.spacing.spacingXxLarge};
    overflow-x: scroll;
  }
`;
